/**
 * Location Redirect Popup Module
 *
 * Handles geolocation-based popup that suggests users switch to their country's site.
 * - Uses Shopify's native browsing_context_suggestions.json API for geolocation
 * - No external API calls, no rate limits, GDPR compliant
 * - Compares detected country to current site locale
 * - Prompts user to switch or continue
 * - Stores choice in sessionStorage (clears when browser session ends)
 *
 * @module location-redirect-popup
 */

import { SELECTORS, POPUP_MODES } from './constants.js';
import type { PopupMode } from './constants.js';
import { getStoredData, saveChoice, clearChoice } from './storage.js';
import { parseMarketsData, detectCountry, parseWelcomeStrings } from './geolocation.js';
import {
  populateContent,
  populateWelcomeContent,
  populateUnsupportedLanguageContent,
  bindEvents,
  show,
} from './ui.js';
import {
  detectUnsupportedLanguageRedirect,
  clearSourceLocaleParam,
  parseUnsupportedLanguageStrings,
} from './unsupported-language.js';
import type { MarketsData } from './geolocation.js';
import type { PopupState } from './ui.js';

// Re-export commonly used functions for external use and testing
export {
  STORAGE_KEY,
  LOCALE_PATTERN,
  SELECTORS,
  CLASSES,
  FOCUSABLE,
  CHOICE_SAVED_EVENT,
  POPUP_MODES,
  SOURCE_LOCALE_PARAM,
} from './constants.js';
export {
  getStoredData,
  getStoredCountry,
  saveChoice,
  clearChoice,
  savePendingSwitch,
} from './storage.js';
export {
  parseMarketsData,
  detectCountry,
  buildRedirectUrl,
  parseStringsFromScript,
  parseWelcomeStrings,
  interpolate,
} from './geolocation.js';
export {
  populateContent,
  populateWelcomeContent,
  populateUnsupportedLanguageContent,
  show,
  hide,
  bindEvents,
} from './ui.js';
export {
  detectUnsupportedLanguageRedirect,
  clearSourceLocaleParam,
  parseUnsupportedLanguageStrings,
  getSisterSiteDomains,
} from './unsupported-language.js';

/**
 * Builds common popup state from element data attributes
 * @param {HTMLElement} popup - The popup element
 * @param {string} mode - One of POPUP_MODES values
 * @param {Object|null} [targetMarket] - Target market info
 * @returns {PopupState}
 */
function buildState(
  popup: HTMLElement,
  mode: PopupMode,
  targetMarket: PopupState['targetMarket'] = null,
): PopupState {
  return {
    currentCountry: popup.dataset.currentCountry!,
    currentCountryName: popup.dataset.currentCountryName!,
    currentCurrencyCode: popup.dataset.currentCurrencyCode!,
    currentCurrencyName: popup.dataset.currentCurrencyName!,
    currentLocale: popup.dataset.currentLocale!,
    targetMarket: targetMarket,
    mode: mode,
    previousActiveElement: null,
    originalOverflow: '',
  };
}

/**
 * Finds a market by country code and shows the main popup for it
 * Used for both pending confirmation (footer switch) and previous selection flows
 * @param {HTMLElement} popup - The popup element
 * @param {string} country - The country code to look up
 * @param {Object} marketsData - Parsed markets data
 * @param {string} mode - One of POPUP_MODES values
 * @param {string} skipMessage - Console message if country not found in markets
 * @returns {boolean} Whether popup was shown
 */
function showMarketPopup(
  popup: HTMLElement,
  country: string,
  marketsData: MarketsData,
  mode: PopupMode,
  skipMessage: string,
): boolean {
  const targetMarket = marketsData.markets.find(function (m) {
    return m.iso_code.toUpperCase() === country.toUpperCase();
  });

  if (!targetMarket) {
    console.info('[Location Redirect] ' + skipMessage);
    return false;
  }

  targetMarket.displayName = targetMarket.name;

  const state = buildState(popup, mode, targetMarket as NonNullable<PopupState['targetMarket']>);
  populateContent(popup, state);
  bindEvents(popup, state);
  show(popup, state);

  return true;
}

/**
 * Shows popup for users arriving from a sister site with a non-English locale
 * @param {HTMLElement} unsupportedLangPopup - The unsupported language popup element
 * @returns {boolean} Whether popup was shown
 */
function showUnsupportedLanguagePopup(unsupportedLangPopup: HTMLElement | null): boolean {
  const strings = parseUnsupportedLanguageStrings();
  if (!unsupportedLangPopup || !strings) {
    console.info(
      '[Location Redirect] Unsupported language popup element or strings not found, skipping',
    );
    return false;
  }

  const state = buildState(unsupportedLangPopup, POPUP_MODES.UNSUPPORTED_LANGUAGE);
  populateUnsupportedLanguageContent(unsupportedLangPopup, state, strings);
  bindEvents(unsupportedLangPopup, state);
  show(unsupportedLangPopup, state);

  return true;
}

/**
 * Shows popup based on geolocation detection (welcome popup for first-time visitors)
 * @param {HTMLElement} popup - The main popup element (used for data attributes)
 * @param {HTMLElement} welcomePopup - The welcome popup element to display
 * @param {Object} marketsData - Parsed markets data
 * @returns {Promise<boolean>} Whether popup was shown
 */
async function showGeolocationPopup(
  popup: HTMLElement,
  welcomePopup: HTMLElement | null,
  marketsData: MarketsData,
): Promise<boolean> {
  const suggestion = await detectCountry(popup.dataset.currentCountry!);
  if (!suggestion) {
    console.info('[Location Redirect] Could not detect country, skipping');
    return false;
  }

  const detectedCode = suggestion.detected_values?.country?.handle;
  const detectedName = suggestion.detected_values?.country?.name;
  if (!detectedCode) {
    console.info('[Location Redirect] No detected country code, skipping');
    return false;
  }

  if (detectedCode.toUpperCase() === popup.dataset.currentCountry!.toUpperCase()) {
    console.info('[Location Redirect] User is in current country, skipping');
    return false;
  }

  const targetMarket = marketsData.markets.find(function (m) {
    return m.iso_code.toUpperCase() === detectedCode.toUpperCase();
  });
  if (!targetMarket) {
    console.info('[Location Redirect] Detected country not in available markets, skipping');
    return false;
  }

  targetMarket.displayName = detectedName || targetMarket.name;

  const state = buildState(
    popup,
    POPUP_MODES.GEOLOCATION,
    targetMarket as NonNullable<PopupState['targetMarket']>,
  );

  const welcomeStrings = parseWelcomeStrings();
  if (welcomePopup && welcomeStrings) {
    populateWelcomeContent(welcomePopup, state, welcomeStrings);
    bindEvents(welcomePopup, state);
    show(welcomePopup, state);
  } else {
    // Fallback to main popup if welcome popup/strings not available
    populateContent(popup, state);
    bindEvents(popup, state);
    show(popup, state);
  }

  return true;
}

/**
 * Main initialization function
 * @returns {Promise<void>}
 */
export async function init() {
  console.info('[Location Redirect] Initializing');

  const popup = document.querySelector<HTMLElement>(SELECTORS.popup);
  if (!popup) {
    console.info('[Location Redirect] Popup element not found, skipping');
    return;
  }

  const welcomePopup = document.querySelector<HTMLElement>(SELECTORS.welcomePopup);
  const unsupportedLanguagePopup = document.querySelector<HTMLElement>(
    SELECTORS.unsupportedLanguagePopup,
  );

  const marketsData = parseMarketsData();
  if (!marketsData) {
    saveChoice(popup.dataset.currentCountry!);
    console.info('[Location Redirect] No markets data, skipping');
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);

  // Debug mode: ?debug_location_redirect=CA bypasses all guards and shows popup immediately
  const debugCountry = searchParams.get('debug_location_redirect');
  if (debugCountry) {
    const targetMarket = marketsData.markets.find(function (m) {
      return m.iso_code.toUpperCase() === debugCountry.toUpperCase();
    });
    if (!targetMarket) {
      console.warn(
        '[Location Redirect] debug_location_redirect: country code not found in markets:',
        debugCountry,
      );
      return;
    }
    targetMarket.displayName = targetMarket.name;
    const debugState = buildState(
      popup,
      POPUP_MODES.GEOLOCATION,
      targetMarket as NonNullable<PopupState['targetMarket']>,
    );
    const debugWelcomeStrings = parseWelcomeStrings();
    if (welcomePopup && debugWelcomeStrings) {
      populateWelcomeContent(welcomePopup, debugState, debugWelcomeStrings);
      bindEvents(welcomePopup, debugState);
      show(welcomePopup, debugState);
    } else {
      populateContent(popup, debugState);
      bindEvents(popup, debugState);
      show(popup, debugState);
    }
    console.info('[Location Redirect] Debug mode: showing popup for', debugCountry);
    return;
  }

  // Debug mode: ?debug_unsupported_language=1 shows the unsupported language popup immediately
  const debugUnsupportedLang = searchParams.get('debug_unsupported_language');
  if (debugUnsupportedLang) {
    const shown = showUnsupportedLanguagePopup(unsupportedLanguagePopup);
    if (shown) {
      console.info('[Location Redirect] Debug mode: showing unsupported language popup');
    }
    return;
  }

  const storedData = getStoredData();
  const currentCountry = popup.dataset.currentCountry!;

  // Phase 0: Check for unsupported language redirect (via source_locale param or sister site referrer)
  const langRedirect = detectUnsupportedLanguageRedirect();
  if (langRedirect.detected) {
    const shown = showUnsupportedLanguagePopup(unsupportedLanguagePopup);
    if (shown) {
      clearSourceLocaleParam();
      console.info('[Location Redirect] Initialized (unsupported language mode)');
      return;
    }
  }

  // Phase 1: Check for pending confirmation (user just switched via footer)
  if (storedData?.pendingFrom) {
    // User came from another site via footer selection
    if (storedData.pendingFrom.toUpperCase() === currentCountry.toUpperCase()) {
      // User switched to same country (edge case), skip
      saveChoice(currentCountry);
      console.info('[Location Redirect] Pending switch to same country, skipping');
      return;
    }

    const shown = showMarketPopup(
      popup,
      storedData.pendingFrom,
      marketsData,
      POPUP_MODES.PENDING_CONFIRMATION,
      'Previous country not in available markets, skipping pending confirmation',
    );
    if (shown) {
      console.info('[Location Redirect] Initialized (pending confirmation mode)');
      return;
    }
    // If previous country not in markets, fall through to geolocation
  }

  // Phase 2: Check for confirmed previous selection mismatch
  if (storedData?.country) {
    if (storedData.country.toUpperCase() === currentCountry.toUpperCase()) {
      console.info('[Location Redirect] User already confirmed this country, skipping');
      return;
    }

    // Confirmed country differs from current — clear so downstream features
    // (e.g. popup-custom) know the choice is unresolved while the popup is open
    clearChoice();
    const shown = showMarketPopup(
      popup,
      storedData.country,
      marketsData,
      POPUP_MODES.PREVIOUS_SELECTION,
      'Stored country not in available markets, skipping',
    );
    if (shown) {
      console.info('[Location Redirect] Initialized (previous selection mode)');
      return;
    }
    // If stored country not in markets, fall through to geolocation
  }

  // Phase 3: No stored data (or invalid) - use geolocation detection (welcome popup)
  const shown = await showGeolocationPopup(popup, welcomePopup, marketsData);
  if (shown) {
    console.info('[Location Redirect] Initialized');
  } else {
    saveChoice(currentCountry);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  void init();
}
